import { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  getReferencedContextParams,
  PostCallActionType,
  PostCallCustomApiConfig,
  PostCallFinalizedAction,
  ProviderConfig,
  type Agent,
} from 'src/interfaces/agent.interface';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { SelectBox } from 'src/components/SelectBox';
import { Textarea } from 'src/components/Textarea';
import { useIntegrationAppProviders } from 'src/hooks/useIntegrationAppProviders';
import { ProviderSelect } from 'src/pages/Automations/components/ui/ProviderSelect';
import { cn } from 'src/utils/className';
import { Button } from 'src/components/Button';
import { useIntegrationApp } from '@membranehq/react';
import { IntegrationAppActionKey } from 'src/services/integration-app.service';
import { Modal } from 'src/components/Modal';
import { X } from '@phosphor-icons/react';
import Accordion from 'src/components/Accordion';
import { POST_CALL_ACTION_VARIABLES } from 'src/components/PostCallActionModal';
import { PromptTextArea } from 'src/components/PromptTextArea';
import { IconButton } from 'src/components/IconButton';

type CustomPostCallApiSectionProps = {
  agent: Agent;
  setAgent: (value: React.SetStateAction<Agent>) => void;
  loading?: boolean;
};

const DEFAULT_PROVIDER_OPTION = { value: '', label: 'Select a provider' };
const toInsightVariable = (analysisItemId: string) =>
  `{insight['${analysisItemId}']}`;

const HTTP_METHODS: { value: string; label: string }[] = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'PUT', label: 'PUT' },
  { value: 'DELETE', label: 'DELETE' },
];

const BODY_METHODS = ['POST', 'PATCH', 'PUT'];

// Activity actions lends itself to multiple actions, but we only support one custom API action now.
const getFirstCustomApiAction = (
  agent: Agent,
): PostCallFinalizedAction | undefined => {
  return agent?.post_call_actions?.find(
    (action) => action.type === PostCallActionType.TRIGGER_CUSTOM_API,
  );
};

const updateCustomApiAction = (
  agent: Agent,
  config: Partial<PostCallCustomApiConfig> & { destination_id?: string },
): Agent => {
  const { destination_id, ...actionConfigUpdates } = config;
  const existing = agent.post_call_actions ?? [];
  const existingAction = getFirstCustomApiAction(agent);
  const existingActionConfig =
    existingAction?.config as PostCallCustomApiConfig; // trigger_custom_api must use PostCallCustomApiConfig format
  const mergedProviderConfig: ProviderConfig = {
    destination_id:
      destination_id ?? existingAction?.provider_config?.destination_id ?? '',
  };
  const mergedActionConfig: PostCallCustomApiConfig = {
    http_method: existingActionConfig?.http_method ?? 'POST',
    relative_uri: existingActionConfig?.relative_uri ?? '',
    body: existingActionConfig?.body,
    headers: existingActionConfig?.headers,
    ...actionConfigUpdates,
  };
  const rest = existing.filter(
    (a) => a.type !== PostCallActionType.TRIGGER_CUSTOM_API,
  );
  return {
    ...agent,
    post_call_actions: [
      ...rest,
      {
        id: existingAction?.id ?? uuidv4(),
        label: 'Custom API Trigger',
        conditions: null,
        type: PostCallActionType.TRIGGER_CUSTOM_API,
        provider_config: mergedProviderConfig,
        config: mergedActionConfig,
      },
    ],
  };
};

const getJsonValidationError = (value?: string): string | null => {
  if (!value || !value.trim()) return null;
  try {
    JSON.parse(value);
    return null;
  } catch {
    return 'Invalid JSON';
  }
};

function parseHeadersFromConfig(
  value: string | undefined,
): { name: string; value: unknown }[] {
  if (!value?.trim()) return [];
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))
      return [];
    return Object.entries(parsed).map(([name, value]) => ({ name, value }));
  } catch {
    return [];
  }
}

export const CustomPostCallApiSection = ({
  agent,
  setAgent,
  loading = false,
}: CustomPostCallApiSectionProps) => {
  const { customApiProviders } = useIntegrationAppProviders();
  const integrationApp = useIntegrationApp();
  const [testResult, setTestResult] = useState<string>('');
  const [testResultsModalOpen, setTestResultsModalOpen] = useState(false);
  const [bodyError, setBodyError] = useState<string | null>(null);
  const customApiAction = getFirstCustomApiAction(agent);
  const config = customApiAction?.config as PostCallCustomApiConfig;
  const headers = useMemo(
    () => parseHeadersFromConfig(config?.headers),
    [config?.headers],
  );

  const setHeaders = (next: { name: string; value: unknown }[]) => {
    setAgent(
      updateCustomApiAction(agent, {
        headers:
          next.length > 0
            ? JSON.stringify(
                next.reduce(
                  (acc, curr) => ({ ...acc, [curr.name]: curr.value }),
                  {} as Record<string, unknown>,
                ),
              )
            : undefined,
      }),
    );
  };

  const destinationId = customApiAction?.provider_config?.destination_id ?? '';
  const httpMethod = config?.http_method ?? 'POST';
  const relativeUri = config?.relative_uri ?? '';
  const body = config?.body ?? '';
  const showBody = BODY_METHODS.includes(httpMethod);

  const selectedProvider =
    customApiProviders.find((option) => option.value === destinationId) ??
    DEFAULT_PROVIDER_OPTION;

  const isValidForm = useMemo(() => {
    return (
      relativeUri.trim() !== '' && relativeUri.startsWith('/') && !bodyError
    );
  }, [relativeUri, bodyError]);

  // Retrieve usable variables
  const insightVariables = useMemo(() => {
    if (
      !agent.post_call_analysis_items ||
      agent.post_call_analysis_items.length === 0
    )
      return [];
    return agent.post_call_analysis_items
      .map((i) => toInsightVariable(i.id))
      .sort((a, b) => a.localeCompare(b));
  }, [agent.post_call_analysis_items]);

  const contextVariables = useMemo(
    () =>
      getReferencedContextParams(agent)
        .map((param) => `{${param}}`)
        .sort((a, b) => a.localeCompare(b)),
    [agent],
  );

  const postCallActionVariables = useMemo(
    () =>
      Array.from(
        new Set([
          ...contextVariables,
          ...insightVariables,
          ...POST_CALL_ACTION_VARIABLES,
        ]),
      ),
    [contextVariables, insightVariables],
  );

  return (
    <div className={cn(loading && 'opacity-50')}>
      <h4>Custom API Trigger</h4>
      <div className="text-neutral-400 text-sm">
        Configure a custom API to be called after call activities are completed.
      </div>

      <ProviderSelect
        key={agent.id}
        label=""
        htmlFor="custom-api-provider"
        providers={customApiProviders}
        selectedProvider={selectedProvider}
        onChange={(e) => {
          if (!agent) return;
          if (!e.value) {
            setAgent({
              ...agent,
              post_call_actions: (agent.post_call_actions ?? []).filter(
                (a) => a.type !== PostCallActionType.TRIGGER_CUSTOM_API,
              ),
            });
            return;
          }
          setAgent(updateCustomApiAction(agent, { destination_id: e.value }));
        }}
      />

      {selectedProvider && selectedProvider.value && (
        <>
          <div className="flex gap-2 mb-4 ">
            <div className="flex flex-col">
              <Label>Method</Label>
              <SelectBox
                options={HTTP_METHODS}
                value={HTTP_METHODS.find((m) => m.value === httpMethod) ?? null}
                onChange={(value) => {
                  if (!agent) return;
                  setBodyError(null);
                  setAgent(
                    updateCustomApiAction(agent, {
                      http_method: value,
                      ...(BODY_METHODS.includes(value) ? {} : { body: '' }),
                    }),
                  );
                }}
              />
            </div>
            <Input
              label="Relative URI"
              placeholder="/v1/events"
              value={relativeUri}
              onChange={(e) => {
                if (!agent) return;
                setAgent(
                  updateCustomApiAction(agent, {
                    relative_uri: e.target.value,
                  }),
                );
              }}
              required
            />
          </div>

          {showBody && (
            <div className="flex flex-col gap-2">
              <div className="flex justify-between">
                <Label htmlFor="custom-api-body">JSON payload</Label>
                {bodyError && (
                  <div className="text-error text-sm">{bodyError}</div>
                )}
              </div>
              <PromptTextArea
                id="custom-api-body"
                placeholder='{"key": "value"}'
                value={body}
                rows={6}
                variables={Array.from(new Set([...postCallActionVariables]))}
                className="font-mono text-sm"
                onChange={(e) => {
                  if (!agent) return;
                  setBodyError(getJsonValidationError(e.target.value));
                  setAgent(
                    updateCustomApiAction(agent, { body: e.target.value }),
                  );
                }}
                onBlur={(e) =>
                  setBodyError(getJsonValidationError(e.target.value))
                }
              />
            </div>
          )}
          <Accordion
            title="Custom Headers"
            defaultOpen={false}
            className="border-none"
          >
            {headers.map((header, index) => (
              <div className="flex gap-2 mb-2 items-center" key={index}>
                <Input
                  placeholder="Header name"
                  value={header.name}
                  onChange={(e) =>
                    setHeaders(
                      headers.map((h, i) =>
                        i === index
                          ? { name: e.target.value, value: h.value }
                          : h,
                      ),
                    )
                  }
                />
                <Input
                  placeholder="Header value"
                  value={`${header.value}`}
                  onChange={(e) =>
                    setHeaders(
                      headers.map((h, i) =>
                        i === index
                          ? { name: h.name, value: e.target.value }
                          : h,
                      ),
                    )
                  }
                />
                <IconButton
                  aria-label="Remove header"
                  className="text-error"
                  onClick={() =>
                    setHeaders(headers.filter((_, i) => i !== index))
                  }
                >
                  <X className="h-4 w-4" />
                </IconButton>
              </div>
            ))}
            <Button
              className="mt-4 w-full"
              variant="outlined"
              color="secondary"
              onClick={() => setHeaders([...headers, { name: '', value: '' }])}
            >
              + Add Header
            </Button>
          </Accordion>

          <div className="flex justify-end">
            <Button
              disabled={!isValidForm}
              onClick={async () => {
                try {
                  if (!config) {
                    setTestResult('ERROR: Invalid or missing configuration');
                    return;
                  }
                  const { output } = await integrationApp
                    .connection(destinationId)
                    .action(IntegrationAppActionKey.CUSTOM_API)
                    .run({
                      payload: config?.body,
                      headers: config?.headers,
                      method: httpMethod,
                      uri: relativeUri,
                    });
                  setTestResult(output || 'Success');
                } catch (error) {
                  setTestResult(
                    `ERROR: ${error instanceof Error ? error.message : 'Unknown error'}`,
                  );
                } finally {
                  setTestResultsModalOpen(true);
                }
              }}
            >
              Test
            </Button>
          </div>
        </>
      )}
      <Modal
        title="Test Results"
        isOpen={testResultsModalOpen}
        onClose={() => setTestResultsModalOpen(false)}
      >
        <Textarea
          value={JSON.stringify(testResult, null, 2)}
          readOnly
          rows={12}
          className="resize-none font-mono text-sm"
        />
      </Modal>
    </div>
  );
};
